import { useEffect, useMemo, useState } from "react";
import type { AuthSessionResponse } from "@yagni/shared";
import { ArrowRight, Building2, Check, UsersRound } from "lucide-react";
import { readJsonError } from "../lib/readJsonError.js";
import { AUTHENTICATED_DEFAULT_PATH } from "../routes/defaults.js";
import { Button, Card } from "../ui/index.js";

/**
 * Read-only look at what sits behind the claim link (onboarding 1B, Act C):
 * the plan's contents for the "This plan" box, and whether one of the user's
 * workspaces was auto-created from this very claim at signup, so the card can
 * say so instead of presenting that workspace as a pre-existing association.
 * Fail-soft: when the preview cannot load, the card renders without it.
 */
interface ClaimPreview {
  companyName: string;
  teamNames: string[];
  primaryTeamName: string | null;
  hasTeamBlueprint: boolean;
  claimCarriedWorkspaceId: string | null;
}

export function ClaimTeamRoomImport({
  session,
  claimToken,
  onImported = () => window.location.assign("/team-room/setup"),
}: {
  session: AuthSessionResponse;
  claimToken: string;
  onImported?: () => void;
}) {
  const eligible = useMemo(
    () => session.memberships.filter(
      (membership) => membership.role === "owner" || membership.role === "admin",
    ),
    [session.memberships],
  );
  const activeEligible = eligible.find(
    (membership) => membership.workspace.id === session.workspace.id,
  );
  const [workspaceId, setWorkspaceId] = useState(
    activeEligible?.workspace.id ?? eligible[0]?.workspace.id ?? "",
  );
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(session.workspace.id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [terminal, setTerminal] = useState<
    "not_claimable" | "already_seeded" | "single_team_plan" | null
  >(null);
  const [preview, setPreview] = useState<ClaimPreview | null>(null);

  useEffect(() => {
    // Strictly decorative and strictly fail-soft: any failure here (including
    // a 404 — the artifact may have expired, or a transient lookup error
    // degraded to not-found) leaves the card fully functional. Terminal
    // states belong to the import action, where they are checked at the
    // moment they matter.
    const controller = new AbortController();
    void (async () => {
      try {
        const response = await fetch(
          `/api/workspace/onboarding/team-blueprint/claim-preview?claimToken=${encodeURIComponent(claimToken)}`,
          { credentials: "include", signal: controller.signal },
        );
        if (!response.ok) return;
        const body = (await response.json().catch(() => null)) as ClaimPreview | null;
        if (!controller.signal.aborted && body && Array.isArray(body.teamNames)) {
          setPreview(body);
        }
      } catch {
        // Aborted or failed; the import path stays fully functional.
      }
    })();
    return () => controller.abort();
  }, [claimToken]);

  // The claim-carry signup created a workspace for this exact plan and the
  // user owns it: say that plainly and skip the workspace picker.
  const carriedMembership = preview?.claimCarriedWorkspaceId
    ? eligible.find(
        (membership) => membership.workspace.id === preview.claimCarriedWorkspaceId,
      )
    : undefined;

  // Enter the claim-carried workspace directly (no import): the blueprint-less
  // plan already seeded it at signup, so there is no room review to run.
  const openWorkspace = async (targetWorkspaceId: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (targetWorkspaceId !== activeWorkspaceId) {
        const switched = await fetch("/api/auth/switch-workspace", {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ workspaceId: targetWorkspaceId }),
        });
        if (!switched.ok) throw new Error("workspace_switch_failed");
        setActiveWorkspaceId(targetWorkspaceId);
      }
      window.location.assign(AUTHENTICATED_DEFAULT_PATH);
    } catch {
      setError("We couldn't open the workspace. Check your connection and try again.");
      setBusy(false);
    }
  };

  const importRoom = async (targetWorkspaceId?: string) => {
    const intoWorkspaceId = targetWorkspaceId ?? workspaceId;
    if (!intoWorkspaceId || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (intoWorkspaceId !== activeWorkspaceId) {
        const switched = await fetch("/api/auth/switch-workspace", {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ workspaceId: intoWorkspaceId }),
        });
        if (!switched.ok) throw new Error("workspace_switch_failed");
        // The session cookie changes even if the subsequent import fails. Keep
        // the client-side pointer honest so choosing another workspace always
        // switches the server back before retrying.
        setActiveWorkspaceId(intoWorkspaceId);
      }
      const imported = await fetch("/api/workspace/onboarding/team-blueprint/import", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ claimToken }),
      });
      if (imported.status === 404) {
        // No claimable Team plan behind this token: expired, revoked, or
        // unknown. Retrying can never succeed, so say so once instead of
        // rendering a retryable-looking error.
        setTerminal("not_claimable");
        return;
      }
      if (imported.status === 409) {
        // A single-Team plan (legacy compile or degraded research) can only
        // seed a workspace at creation. Distinguish it from the generic
        // conflicts, which stay retryable against another workspace.
        const body = (await imported
          .clone()
          .json()
          .catch(() => null)) as { code?: string } | null;
        if (body?.code === "single_team_plan") {
          setTerminal("single_team_plan");
          return;
        }
      }
      if (!imported.ok) {
        setError(await readJsonError(imported));
        return;
      }
      const body = (await imported.json().catch(() => null)) as {
        alreadySeeded?: boolean;
      } | null;
      if (body?.alreadySeeded) {
        // The claim-carry signup already put this plan's starter Team into the
        // workspace. There is no full room to review, so never route onward.
        setTerminal("already_seeded");
        return;
      }
      onImported();
    } catch {
      setError("We couldn't add this Team plan. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const planBox = preview && preview.teamNames.length > 0 && (
    <div className="mt-5 rounded-xl border border-[var(--border)] px-4 py-3.5" data-testid="claim-plan-box">
      <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--text-tertiary)]">
        This plan · {preview.teamNames.length} {preview.teamNames.length === 1 ? "Team" : "Teams"}
      </p>
      <ul className="mt-2 space-y-1.5 text-sm text-[var(--text-secondary)]">
        {preview.teamNames.map((name) => (
          <li key={name} className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--accent)]" aria-hidden="true" />
            {name}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="workspace-shell grid min-h-screen place-items-center [background:var(--canvas-field)] px-5 py-10 text-[var(--text-primary)]">
      <main className="w-full max-w-xl" aria-labelledby="claim-room-title">
        {/* Glass: a claim staged for import, waiting on the human's decision. */}
        <Card variant="staged" padding="lg">
          <div className="mb-5 flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--accent-muted)] text-[var(--accent)]">
              <UsersRound className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--accent)]">
                Researched Teams
              </p>
              <h1 id="claim-room-title" className="mt-1 font-[var(--font-display)] text-2xl font-semibold tracking-[-0.03em]">
                Add this plan to a workspace
              </h1>
              <p className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">
                {preview?.companyName
                  ? `Your Teams for ${preview.companyName} are ready to move in. You review everything before anything is created.`
                  : "You will review the company profile, Teams, Responsibilities, Numbers, Rhythms, and Connections before anything is created."}
              </p>
            </div>
          </div>

          {terminal === "not_claimable" ? (
            <div role="alert" className="rounded-md border border-[var(--warning)]/30 bg-[var(--warning-muted)] px-4 py-3 text-sm leading-6">
              This shared Team plan is no longer available to import. The link may have expired or been revoked. Nothing was changed in your workspace.
              <div className="mt-3"><a className="font-medium underline" href={AUTHENTICATED_DEFAULT_PATH}>Continue to Waiting on you</a></div>
            </div>
          ) : terminal === "already_seeded" ? (
            <div role="alert" className="rounded-md border border-[var(--print-border)] bg-[var(--print-bg)] px-4 py-3 text-sm leading-6">
              This plan is already part of this workspace. Its starter Team came in when the workspace was created, so there is nothing more to import.
              <div className="mt-3"><a className="font-medium underline" href={AUTHENTICATED_DEFAULT_PATH}>Continue to Waiting on you</a></div>
            </div>
          ) : terminal === "single_team_plan" ? (
            <div role="alert" className="rounded-md border border-[var(--warning)]/30 bg-[var(--warning-muted)] px-4 py-3 text-sm leading-6">
              This link holds an early, single starter Team draft, which can only start a brand-new workspace. Nothing was changed in your workspace.
              <div className="mt-3 flex flex-wrap gap-4">
                <a className="font-medium underline" href={AUTHENTICATED_DEFAULT_PATH}>Continue to Waiting on you</a>
              </div>
            </div>
          ) : carriedMembership ? (
            <>
              <div
                className="flex items-start gap-3 rounded-xl border border-[var(--success)]/25 bg-[var(--success-muted)] px-4 py-3.5"
                data-testid="claim-carried-workspace"
              >
                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[var(--success)] text-white">
                  <Check className="h-3 w-3" aria-hidden="true" />
                </span>
                <div className="text-sm leading-6">
                  <p className="font-medium text-[var(--text-primary)]">
                    We created a new workspace &ldquo;{carriedMembership.workspace.name}&rdquo; for this plan. You own it.
                  </p>
                  <p className="text-[var(--text-secondary)]">
                    You can rename it or invite teammates once you are in.
                  </p>
                </div>
              </div>
              {planBox}
              {error && (
                <p role="alert" className="mt-4 rounded-md border border-[var(--warning)]/30 bg-[var(--warning-muted)] px-3 py-2 text-sm">
                  {error}
                </p>
              )}
              <div className="mt-6 flex flex-wrap justify-end gap-2">
                <a href={AUTHENTICATED_DEFAULT_PATH} className="inline-flex h-9 items-center rounded-md px-3 text-sm text-[var(--text-secondary)] hover:bg-[rgba(var(--glass-rgb),0.68)]">
                  Skip for now
                </a>
                {preview?.hasTeamBlueprint ? (
                  <Button
                    type="button"
                    loading={busy}
                    disabled={busy}
                    onClick={() => void importRoom(carriedMembership.workspace.id)}
                  >
                    Review the Teams <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                ) : (
                  // A blueprint-less (single starter Team) claim has no room
                  // review; the plan seeded the workspace at signup, so the
                  // honest action is to go straight in.
                  <Button
                    type="button"
                    loading={busy}
                    disabled={busy}
                    onClick={() => void openWorkspace(carriedMembership.workspace.id)}
                  >
                    Open workspace <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                )}
              </div>
            </>
          ) : eligible.length > 0 ? (
            <>
              <label htmlFor="claim-workspace" className="text-sm font-medium">
                Workspace
              </label>
              <div className="relative mt-2">
                <Building2 className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-[var(--text-tertiary)]" aria-hidden="true" />
                <select
                  id="claim-workspace"
                  value={workspaceId}
                  disabled={busy}
                  onChange={(event) => setWorkspaceId(event.target.value)}
                  className="h-10 w-full rounded-md border border-[var(--border)] bg-[var(--bg-input)] pl-9 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
                >
                  {eligible.map((membership) => (
                    <option key={membership.workspace.id} value={membership.workspace.id}>
                      {membership.workspace.name} · {membership.role}
                    </option>
                  ))}
                </select>
              </div>
              {planBox}
              <p className="mt-3 text-xs leading-5 text-[var(--text-tertiary)]">
                Existing Teams are never changed. If a proposed name already exists, review will ask you to rename it before creating the new Teams.
              </p>
              {error && (
                <p role="alert" className="mt-4 rounded-md border border-[var(--warning)]/30 bg-[var(--warning-muted)] px-3 py-2 text-sm">
                  {error}
                </p>
              )}
              <div className="mt-6 flex flex-wrap justify-end gap-2">
                <a href={AUTHENTICATED_DEFAULT_PATH} className="inline-flex h-9 items-center rounded-md px-3 text-sm text-[var(--text-secondary)] hover:bg-[rgba(var(--glass-rgb),0.68)]">
                  Skip for now
                </a>
                <Button type="button" loading={busy} disabled={busy} onClick={() => void importRoom()}>
                  Review in this workspace <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            </>
          ) : (
            <div role="alert" className="rounded-md border border-[var(--warning)]/30 bg-[var(--warning-muted)] px-4 py-3 text-sm leading-6">
              You need an owner or admin role to add a Team plan. Ask a workspace owner to open the claim link, or continue to YAGNI.
              <div className="mt-3"><a className="font-medium underline" href={AUTHENTICATED_DEFAULT_PATH}>Continue to Waiting on you</a></div>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
